import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import LoopIcon from "@mui/icons-material/Loop";
import LogoutIcon from "@mui/icons-material/Logout";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { isMobile } from "react-device-detect";

import Button from "./forms/buttons/Button";
import CancelButton from "./forms/buttons/CancelButton";
import PurpleButton from "./forms/buttons/PurpleButton";
import Dialog from "./Dialog";
import { authenticationService } from "../services/AuthenticationService";
import { offlineService } from "../services/OfflineService";
import { strings } from "../Strings";

/**
 * ヘッダーコンポーネント
 * @param {Function} onLogout ログアウト時の処理
 * @param {boolean} isNotLogin 未ログインかどうか
 * @param {boolean} isOnline オンラインかどうか
 * @param {number} count 未送信データ件数
 * @class Header
 */
const Header = ({ onLogout, isNotLogin = false, isOnline, count = 0 }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLogoutOpen, setLogoutOpen] = useState(false);
  const [isUserChangeOpen, setUserChangeOpen] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);

  /**
   * 現在の日時を取得して currentTime を更新する
   * @memberof Header
   */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  /**
   * ダイアログを閉じてログアウトする
   * @memberof Header
   */
  const onLogoutClick = () => {
    setLogoutOpen(false);
    setUserChangeOpen(false);
    onLogout();
  };
  return (
    <>
      <header className={`header ${isNotLogin ? "login" : ""}`}>
        <div className="brand-area">
          <div className="brand" aria-label="作業実績登録システム">
            <span className="brand-mark" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none">
                <path
                  d="M24 4 42 14v20L24 44 6 34V14L24 4Z"
                  stroke="currentColor"
                  strokeWidth="3.3"
                  strokeLinejoin="round"
                />
                <path
                  d="M24 4v18m18-8L24 22 6 14m18 8v22"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity=".75"
                />
              </svg>
            </span>
            <span id="app-name">{strings.APP_NAME}</span>
          </div>
          {!isNotLogin && (
            <div className="session-info" aria-label="ログイン情報">
              <span className="session-date" id="nowText">
                {format(currentTime, "yyyy年MM月dd日(E) HH:mm:ss", {
                  locale: ja,
                })}
              </span>
              <span>{`社員番号：${authenticationService.employeeNo}`}</span>
              <span className="session-name">
                {authenticationService.employeeName}さん
              </span>
            </div>
          )}
        </div>

        {!isNotLogin && (
          <div className="header-actions">
            <Button
              title="作業者変更"
              icon="user-change"
              onClick={() => setUserChangeOpen(true)}
            />
            <PurpleButton
              title="業務を終了"
              icon="logout"
              onClick={() => setLogoutOpen(true)}
            />
          </div>
        )}
      </header>
      <Dialog
        title="業務を終了しますか？"
        icon={
          <div className="confirm-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path
                d="M10 17l5-5-5-5M15 12H3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 5h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        }
        open={isLogoutOpen}
        onClose={() => setLogoutOpen(false)}
        actions={
          <>
            <CancelButton onClick={() => setLogoutOpen(false)} />
            <PurpleButton title="業務を終了" onClick={onLogoutClick} />
          </>
        }
      >
        終了すると現在の作業者での操作を終了します。続ける場合はキャンセルしてください。
      </Dialog>
      <Dialog
        title="作業者を変更しますか？"
        icon={
          <div className="confirm-icon blue" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 7h9a4 4 0 0 1 0 8H9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M10 4 7 7l3 3M17 20l3-3-3-3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        }
        open={isUserChangeOpen}
        onClose={() => setUserChangeOpen(false)}
        actions={
          <>
            <CancelButton onClick={() => setUserChangeOpen(false)} />
            <Button title="変更する" onClick={onLogoutClick} />
          </>
        }
      >
        変更すると現在の作業者での操作を終了します。続ける場合はキャンセルしてください。
      </Dialog>
      <Dialog
        title={`${authenticationService.employeeNo} ${authenticationService.employeeName}さん`}
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <div id="header-user-button-dialog-contents">
          <Button
            title="作業者を変更する"
            onClick={() => {
              setDialogOpen(false);
              setUserChangeOpen(true);
            }}
            startIcon={<LoopIcon />}
            size="medium"
          />
          <Button
            title="業務を終了する"
            color="secondary"
            onClick={() => {
              setDialogOpen(false);
              setLogoutOpen(true);
            }}
            startIcon={<LogoutIcon />}
            size="medium"
          />
        </div>
      </Dialog>
    </>
  );
};
// const Footer = () => {
//   return (
//     <div id="footer">
//       <span>(c) 2026 CpointLab Corp. All rights reserved.</span>
//       <NavLink to="/license">license</NavLink>
//     </div>
//   );
// };

/**
 * ページコンテナコンポーネント
 * @param {string} title ページタイトル
 * @param {string} subtitle サブヘッダータイトル
 * @param {Node} children 子要素
 * @param {Node} leftContents サブヘッダーの左要素
 * @param {Node} rightContents サブヘッダーの右要素
 * @param {boolean} [isNotLogin=false] ログインページかどうか
 * @param {boolean} [noSubHeader=false] サブヘッダーなしかどうか
 * @param {boolean} [noBackButton=false] 戻るボタンを非表示にするかどうか
 * @class Container
 */
export default function Container({
  title,
  subtitle,
  children,
  leftContents,
  rightContents,
  isNotLogin = false,
  noSubHeader = false,
  noBackButton,
}) {
  const [isLoading, setLoading] = useState(false);
  const [isOnline, setOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  /**
   * ログイン状態をチェックする
   * @memberof Container
   */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = (await authenticationService.auth()).data;
        if (!result.success) {
          navigate("/login", { replace: true });
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };
    if (!isNotLogin) checkAuth();
  }, [isNotLogin, navigate]);
  /**
   * タイトルを反映する
   * @memberof Container
   */
  useEffect(() => {
    if (title) document.title = `${strings.APP_NAME}｜${title}`;
  }, [title]);
  /**
   * オンライン/オフライン状態を監視する
   * @memberof Container
   */
  useEffect(() => {
    const changeOnline = (flag) => {
      setOnline(flag);
    };
    window.addEventListener("online", () => changeOnline(true));
    window.addEventListener("offline", () => changeOnline(false));
    return () => {
      window.removeEventListener("online", () => changeOnline(false));
      window.removeEventListener("offline", () => changeOnline(false));
    };
  }, []);
  /**
   * オンライン復帰だったら、未送信データを確認・再送する
   * 未送信データの件数を取得する
   * @memberof Container
   */
  useEffect(() => {
    const syncOfflineData = async () => {
      try {
        await offlineService.syncDatas();
        const result = await offlineService.count();
        setCount(result);
      } catch (error) {
        console.error(error);
      }
    };
    if (isOnline) {
      syncOfflineData();
    }
  }, [isOnline]);
  /**
   * ログアウトする
   * @memberof Container
   */
  const onLogout = async () => {
    try {
      setLoading(true);
      await authenticationService.logout();
      navigate("/login", { replace: true });
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <div id="container">
      <Header
        onLogout={onLogout}
        isNotLogin={isNotLogin}
        isOnline={isOnline}
        count={count}
      />
      {!isLoading && (
        <div
          id="inner"
          className={`${isMobile ? "mobile" : ""} ${isNotLogin ? "login" : ""}`}
        >
          {children}
        </div>
      )}
      {/* <Footer /> */}
    </div>
  );
}
